import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Paper,
  IconButton,
} from "@mui/material";
import { useState, useCallback, useEffect, useRef } from "react";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const GeneratorPermitEmailStep = ({
  autoEmailEnabled,
  setAutoEmailEnabled,
  cityEmail, // This will be deprecated but kept for compatibility
  setCityEmail, // This will be deprecated but kept for compatibility
  responseEmail, // This will be deprecated but kept for compatibility
  setResponseEmail, // This will be deprecated but kept for compatibility
  emailRows, // Old prop: array of {cc: '', bcc: ''} - for backward compatibility
  setEmailRows, // Old prop setter - for backward compatibility
  userEmail, // User's email to prefill reply-to
  emailRowOnError = [], // Array of row indices with errors
  setEmailRowOnError, // Function to update error indices
}) => {
  const initializedRef = useRef(false);

  // New format: separate arrays for "to" and "from"
  const [emailToList, setEmailToList] = useState(["bldcodes@sjcfl.us"]);
  const [emailReplyToList, setEmailReplyToList] = useState([userEmail || ""]);

  // Initialize from old format if needed
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (emailRows && emailRows.length > 0) {
      // Migrate from old format to new format
      const toEmails = emailRows.map((row) => row.cc || "").filter(Boolean);
      const replyToEmails = emailRows
        .map((row) => row.bcc || "")
        .filter(Boolean);

      if (toEmails.length > 0) setEmailToList(toEmails);
      if (replyToEmails.length > 0) setEmailReplyToList(replyToEmails);
    } else if (cityEmail || responseEmail) {
      // Migrate from old cityEmail/responseEmail format
      if (cityEmail) setEmailToList([cityEmail]);
      if (responseEmail) setEmailReplyToList([responseEmail]);
    }
  }, [emailRows, cityEmail, responseEmail, userEmail]);

  // Update old format for backwards compatibility
  useEffect(() => {
    if (!setEmailRows) return;

    // Create pairs from separate lists
    const maxLength = Math.max(emailToList.length, emailReplyToList.length);
    const rows = [];
    for (let i = 0; i < maxLength; i++) {
      rows.push({
        cc: emailToList[i] || "",
        bcc: emailReplyToList[i] || "",
      });
    }
    setEmailRows(rows);
  }, [emailToList, emailReplyToList, setEmailRows]);

  const [emailErrors, setEmailErrors] = useState({});

  const validateEmail = useCallback((email) => {
    if (!email) return true; // Empty is valid (optional)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const handleEmailToChange = useCallback(
    (index, value) => {
      const newList = [...emailToList];
      newList[index] = value;
      setEmailToList(newList);

      // Also update old format for backwards compatibility
      if (index === 0) {
        setCityEmail?.(value);
      }

      // Clear error from emailRowOnError if at least one cc is now filled
      if (setEmailRowOnError && emailRowOnError.length > 0) {
        const newList = [...emailToList];
        newList[index] = value;
        const hasAtLeastOneCc = newList.some(
          (email) => email && email.trim() !== ""
        );
        if (hasAtLeastOneCc) {
          setEmailRowOnError((prev) =>
            prev.filter((err) => err.field !== "cc")
          );
        }
      }

      // Validate email
      const errorKey = `to-${index}`;
      if (value && !validateEmail(value)) {
        setEmailErrors((prev) => ({
          ...prev,
          [errorKey]: "Invalid email format",
        }));
      } else {
        setEmailErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    },
    [
      emailToList,
      setCityEmail,
      validateEmail,
      emailRowOnError,
      setEmailRowOnError,
    ]
  );

  const handleEmailReplyToChange = useCallback(
    (index, value) => {
      const newList = [...emailReplyToList];
      newList[index] = value;
      setEmailReplyToList(newList);

      // Also update old format for backwards compatibility
      if (index === 0) {
        setResponseEmail?.(value);
      }

      // Clear error from emailRowOnError if at least one bcc is now filled
      if (setEmailRowOnError && emailRowOnError.length > 0) {
        const newList = [...emailReplyToList];
        newList[index] = value;
        const hasAtLeastOneBcc = newList.some(
          (email) => email && email.trim() !== ""
        );
        if (hasAtLeastOneBcc) {
          setEmailRowOnError((prev) =>
            prev.filter((err) => err.field !== "bcc")
          );
        }
      }

      // Validate email
      const errorKey = `replyto-${index}`;
      if (value && !validateEmail(value)) {
        setEmailErrors((prev) => ({
          ...prev,
          [errorKey]: "Invalid email format",
        }));
      } else {
        setEmailErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    },
    [
      emailReplyToList,
      setResponseEmail,
      validateEmail,
      emailRowOnError,
      setEmailRowOnError,
    ]
  );

  const handleAddEmailTo = useCallback(() => {
    setEmailToList([...emailToList, ""]);
  }, [emailToList]);

  const handleAddEmailReplyTo = useCallback(() => {
    setEmailReplyToList([...emailReplyToList, ""]);
  }, [emailReplyToList]);

  const handleDeleteEmailTo = useCallback(
    (index) => {
      if (emailToList.length === 1) return; // Don't delete the last one
      const newList = emailToList.filter((_, i) => i !== index);
      setEmailToList(newList);

      // Clear errors for this field
      setEmailErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`to-${index}`];
        // Adjust errors for fields after the deleted one
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith("to-")) {
            const fieldIndex = parseInt(key.split("-")[1]);
            if (fieldIndex > index) {
              const newKey = `to-${fieldIndex - 1}`;
              newErrors[newKey] = newErrors[key];
              delete newErrors[key];
            }
          }
        });
        return newErrors;
      });

      // Update emailRowOnError: remove errors for deleted cc field and adjust indices
      if (setEmailRowOnError && emailRowOnError.length > 0) {
        setEmailRowOnError((prev) => {
          return prev
            .filter((err) => {
              // Remove error for the deleted field at this specific index
              return !(err.rowIndex === index && err.field === "cc");
            })
            .map((err) => {
              // Adjust row indices for errors after the deleted row
              if (err.rowIndex > index) {
                return { ...err, rowIndex: err.rowIndex - 1 };
              }
              return err;
            });
        });
      }
    },
    [emailToList, emailRowOnError, setEmailRowOnError]
  );

  const handleDeleteEmailReplyTo = useCallback(
    (index) => {
      if (emailReplyToList.length === 1) return; // Don't delete the last one
      const newList = emailReplyToList.filter((_, i) => i !== index);
      setEmailReplyToList(newList);

      // Clear errors for this field
      setEmailErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`replyto-${index}`];
        // Adjust errors for fields after the deleted one
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith("replyto-")) {
            const fieldIndex = parseInt(key.split("-")[1]);
            if (fieldIndex > index) {
              const newKey = `replyto-${fieldIndex - 1}`;
              newErrors[newKey] = newErrors[key];
              delete newErrors[key];
            }
          }
        });
        return newErrors;
      });

      // Update emailRowOnError: remove errors for deleted bcc field and adjust indices
      if (setEmailRowOnError && emailRowOnError.length > 0) {
        setEmailRowOnError((prev) => {
          return prev
            .filter((err) => {
              // Remove error for the deleted field at this specific index
              return !(err.rowIndex === index && err.field === "bcc");
            })
            .map((err) => {
              // Adjust row indices for errors after the deleted row
              if (err.rowIndex > index) {
                return { ...err, rowIndex: err.rowIndex - 1 };
              }
              return err;
            });
        });
      }
    },
    [emailReplyToList, emailRowOnError, setEmailRowOnError]
  );

  const handleAutoEmailToggle = useCallback(
    (e) => {
      const enabled = e.target.value === "yes";
      setAutoEmailEnabled(enabled);

      // Clear emails and errors when toggling off
      if (!enabled) {
        setEmailToList([""]);
        setEmailReplyToList([userEmail || ""]);
        setCityEmail?.("");
        setResponseEmail?.("");
        setEmailErrors({});
      }
    },
    [setAutoEmailEnabled, setCityEmail, setResponseEmail, userEmail]
  );

  return (
    <Box
      sx={{
        height: "calc(100vh - 400px)",
        overflowY: "auto",
        px: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          py: 2,
          maxWidth: "700px",
          margin: "0 auto",
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <SendIcon sx={{ fontSize: 40 }} />
            <Typography variant="h5" fontWeight="bold">
              Auto-Email Configuration
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Configure automatic email delivery of completed permit forms to the
            city
          </Typography>
        </Paper>

        <Paper elevation={1} sx={{ p: 1, borderRadius: 2 }}>
          <FormControl component="fieldset" fullWidth>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color="primary"
            >
              Would you like to auto-email the permit forms to the city when
              completed?
            </Typography>
            <RadioGroup
              value={autoEmailEnabled ? "yes" : "no"}
              onChange={handleAutoEmailToggle}
              sx={{ mt: 2 }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Yes, auto-email the forms
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Forms will be automatically sent to the city upon
                      completion
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      No, I'll submit manually
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      You can download and submit the forms yourself
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Paper>

        {autoEmailEnabled && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <EmailIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Email Configuration
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Configure the email addresses for sending permit forms and
                  receiving responses. You can add multiple emails
                  independently. Email forms to bldcodes@sjcfl.us.
                </Typography>
              </Box>

              {/* Two Column Layout */}
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* Email To Column */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <SendIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Email To
                    </Typography>
                    <IconButton
                      onClick={handleAddEmailTo}
                      color="primary"
                      size="small"
                      title="Add another Email To address"
                      sx={{ ml: "auto" }}
                      disabled={
                        emailToList.length > 0 &&
                        (!emailToList[emailToList.length - 1] ||
                          emailToList[emailToList.length - 1].trim() === "")
                      }
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1.5 }}
                  >
                    Where permit forms will be sent
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {emailToList.map((email, index) => (
                      <Box
                        key={`to-${index}`}
                        sx={{ display: "flex", gap: 0.5, alignItems: "start" }}
                      >
                        <TextField
                          placeholder="City Email Address"
                          value={email}
                          onChange={(e) =>
                            handleEmailToChange(index, e.target.value)
                          }
                          error={
                            !!emailErrors[`to-${index}`] ||
                            (autoEmailEnabled &&
                              emailRowOnError.some(
                                (err) =>
                                  err.rowIndex === index && err.field === "cc"
                              ) &&
                              !emailToList.some(
                                (email) => email && email.trim() !== ""
                              ))
                          }
                          helperText={
                            emailErrors[`to-${index}`] ||
                            (autoEmailEnabled &&
                            emailRowOnError.some(
                              (err) =>
                                err.rowIndex === index && err.field === "cc"
                            ) &&
                            !emailToList.some(
                              (email) => email && email.trim() !== ""
                            )
                              ? "At least one email address is required when auto-email is enabled"
                              : "")
                          }
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-input::placeholder": {
                              color: (theme) => theme.palette.grey[400],
                              opacity: 1,
                            },
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <Box
                                  sx={{
                                    mr: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <SendIcon color="action" fontSize="small" />
                                </Box>
                              ),
                            },
                          }}
                        />
                        {emailToList.length > 1 && (
                          <IconButton
                            onClick={() => handleDeleteEmailTo(index)}
                            color="error"
                            size="small"
                            disabled={emailToList.length === 1}
                            title={
                              emailToList.length === 1
                                ? "Cannot delete the only email"
                                : "Delete this email"
                            }
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Email Reply To Column */}
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <EmailIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Email Reply To
                    </Typography>
                    <IconButton
                      onClick={handleAddEmailReplyTo}
                      color="primary"
                      size="small"
                      title="Add another Email Reply To address"
                      sx={{ ml: "auto" }}
                      disabled={
                        emailReplyToList.length > 0 &&
                        (!emailReplyToList[emailReplyToList.length - 1] ||
                          emailReplyToList[
                            emailReplyToList.length - 1
                          ].trim() === "")
                      }
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1.5 }}
                  >
                    Where city responses will be directed
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {emailReplyToList.map((email, index) => (
                      <Box
                        key={`replyto-${index}`}
                        sx={{ display: "flex", gap: 0.5, alignItems: "start" }}
                      >
                        <TextField
                          placeholder="your-email@company.com"
                          value={email}
                          onChange={(e) =>
                            handleEmailReplyToChange(index, e.target.value)
                          }
                          error={
                            !!emailErrors[`replyto-${index}`] ||
                            (autoEmailEnabled &&
                              emailRowOnError.some(
                                (err) =>
                                  err.rowIndex === index && err.field === "bcc"
                              ) &&
                              !emailReplyToList.some(
                                (email) => email && email.trim() !== ""
                              ))
                          }
                          helperText={
                            emailErrors[`replyto-${index}`] ||
                            (autoEmailEnabled &&
                            emailRowOnError.some(
                              (err) =>
                                err.rowIndex === index && err.field === "bcc"
                            ) &&
                            !emailReplyToList.some(
                              (email) => email && email.trim() !== ""
                            )
                              ? "At least one email address is required when auto-email is enabled"
                              : "")
                          }
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiInputBase-input::placeholder": {
                              color: (theme) => theme.palette.grey[400],
                              opacity: 1,
                            },
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <Box
                                  sx={{
                                    mr: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <EmailIcon color="action" fontSize="small" />
                                </Box>
                              ),
                            },
                          }}
                        />
                        {emailReplyToList.length > 1 && (
                          <IconButton
                            onClick={() => handleDeleteEmailReplyTo(index)}
                            color="error"
                            size="small"
                            disabled={emailReplyToList.length === 1}
                            title={
                              emailReplyToList.length === 1
                                ? "Cannot delete the only email"
                                : "Delete this email"
                            }
                            sx={{ mt: 0.5 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default GeneratorPermitEmailStep;
